"use client";

import { create } from "zustand";

import {
  createDefaultChannel,
  type DataloggerAlertCondition,
  type DataloggerAlertAudiencePrefs,
  type DataloggerAlertRecipients,
  nextUniqueChannelKey,
  samplesPerPeriodToHz,
  type ChannelRow,
  type DataloggerBasic,
  type DataloggerFormSnapshot,
  type DataloggerLifecycle,
  type DataloggerQrPublic,
  type DataloggerProvisioningResult,
  type DataloggerVisualizations,
  type HardwareProtocolSettings,
  type SamplingRatePeriod,
  type TransmissionMode,
} from "../schema/datalogger.schema";

type HardwareDraft = {
  samplingRateValue: number;
  samplingRatePeriod: SamplingRatePeriod;
  transmissionMode: TransmissionMode;
  protocolSettings: HardwareProtocolSettings;
};

function revokePreviewUrl(url: string | null | undefined) {
  if (typeof url === "string" && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
}

function initialHardwareDraft(): HardwareDraft {
  return {
    samplingRateValue: 1,
    samplingRatePeriod: "second",
    transmissionMode: "mqtt",
    protocolSettings: {
      mqttPort: "1883",
    },
  };
}

function initialAlertCondition(channel?: ChannelRow): DataloggerAlertCondition {
  return {
    enabled: true,
    channelId: channel?.id ?? null,
    channelKey: channel?.channelKey ?? "channel_1",
    operator: ">",
    threshold: "0",
    source: "manual",
  };
}

function initialLifecycle(): DataloggerLifecycle {
  const today = new Date();
  const nextCalibration = new Date(today);
  nextCalibration.setMonth(nextCalibration.getMonth() + 6);

  return {
    installationDate: toDateInputValue(today),
    warrantyEnd: "",
    nextCalibration: toDateInputValue(nextCalibration),
  };
}

function initialNotificationPrefs(defaults?: Partial<DataloggerAlertAudiencePrefs>): DataloggerAlertAudiencePrefs {
  return {
    email: defaults?.email ?? false,
    message: defaults?.message ?? false,
    inApp: defaults?.inApp ?? false,
    selectAll: defaults?.selectAll ?? false,
    userIds: defaults?.userIds ? [...defaults.userIds] : [],
  };
}

function initialAlertRecipients(): DataloggerAlertRecipients {
  return {
    admin: initialNotificationPrefs({ email: true, inApp: true }),
    distributor: initialNotificationPrefs(),
    users: initialNotificationPrefs({ inApp: true }),
    emailRecipients: "",
    messageRecipients: "",
  };
}

function initialVisualizations(): DataloggerVisualizations {
  return {
    windRose: {
      enabled: false,
      speedChannelKey: "",
      directionChannelKey: "",
    },
  };
}

function initialQrPublic(): DataloggerQrPublic {
  return { enabled: true, displayFields: ["image", "name", "location", "status", "last_seen"] };
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function syncThresholdAlertConditions(channels: ChannelRow[], alerts: DataloggerAlertCondition[]): DataloggerAlertCondition[] {
  const manualAlerts = alerts.filter((alert) => alert.source === "manual");
  const derivedAlerts = alerts.filter((alert) => alert.source !== "manual");

  const nextDerived = channels.flatMap((channel) => {
    const items: DataloggerAlertCondition[] = [];
    const existingLow = derivedAlerts.find((alert) => alert.channelId === channel.id && alert.source === "warning_low");
    const existingHigh = derivedAlerts.find((alert) => alert.channelId === channel.id && alert.source === "warning_high");

    if (channel.thresholdWarningLow != null) {
      items.push({
        enabled: existingLow?.enabled ?? true,
        channelId: channel.id,
        channelKey: channel.channelKey,
        operator: existingLow?.operator ?? "<",
        threshold: String(channel.thresholdWarningLow),
        source: "warning_low",
      });
    }

    if (channel.thresholdWarningHigh != null) {
      items.push({
        enabled: existingHigh?.enabled ?? true,
        channelId: channel.id,
        channelKey: channel.channelKey,
        operator: existingHigh?.operator ?? ">",
        threshold: String(channel.thresholdWarningHigh),
        source: "warning_high",
      });
    }

    return items;
  });

  return [...nextDerived, ...manualAlerts.map((alert) => ({
    ...alert,
    channelId:
      alert.channelId && channels.some((channel) => channel.id === alert.channelId)
        ? alert.channelId
        : channels.find((channel) => channel.channelKey === alert.channelKey)?.id ?? channels[0]?.id ?? null,
    channelKey: channels.some((channel) => channel.channelKey === alert.channelKey)
      ? alert.channelKey
      : channels[0]?.channelKey ?? "",
  }))];
}

function initialState(): {
  step: number;
  basic: DataloggerBasic;
  channels: ChannelRow[];
  hardwareDraft: HardwareDraft;
  lifecycle: DataloggerLifecycle;
  alertConditions: DataloggerAlertCondition[];
  alertRecipients: DataloggerAlertRecipients;
  visualizations: DataloggerVisualizations;
  qrPublic: DataloggerQrPublic;
  provisioningResult: DataloggerProvisioningResult | null;
} {
  const channels = [createDefaultChannel(0, [])];
  return {
    step: 0,
    basic: {
      name: "",
      deviceCategoryId: "",
      description: "",
      simType: "phone",
      phoneNumber: "",
      imeiNumber: "",
      location: "",
      city: "",
      stateId: "",
      state: "",
      tags: [],
      latitude: "",
      longitude: "",
      deviceImagePath: null,
      deviceImagePreviewUrl: null,
    },
    channels,
    hardwareDraft: initialHardwareDraft(),
    lifecycle: initialLifecycle(),
    alertConditions: syncThresholdAlertConditions(channels, []),
    alertRecipients: initialAlertRecipients(),
    visualizations: initialVisualizations(),
    qrPublic: initialQrPublic(),
    provisioningResult: null,
  };
}

type WizardStore = ReturnType<typeof initialState> & {
  setStep: (step: number) => void;
  loadSnapshot: (snapshot: DataloggerFormSnapshot, step?: number) => void;
  setBasic: (patch: Partial<DataloggerBasic>) => void;
  addTag: (tag: string) => void;
  removeTag: (tag: string) => void;
  setHardwareDraft: (patch: Partial<HardwareDraft>) => void;
  setLifecycle: (patch: Partial<DataloggerLifecycle>) => void;
  setProtocolSettings: (patch: Partial<HardwareProtocolSettings>) => void;
  setTransmissionMode: (mode: TransmissionMode) => void;
  setAlertCondition: (index: number, patch: Partial<DataloggerAlertCondition>) => void;
  addAlertCondition: () => void;
  removeAlertCondition: (index: number) => void;
  setAlertRecipients: (patch: Partial<DataloggerAlertRecipients>) => void;
  setVisualizations: (patch: Partial<DataloggerVisualizations>) => void;
  setQrPublic: (patch: Partial<DataloggerQrPublic>) => void;
  setWindRose: (patch: Partial<DataloggerVisualizations["windRose"]>) => void;
  setProvisioningResult: (result: DataloggerProvisioningResult | null) => void;
  setChannels: (channels: ChannelRow[]) => void;
  updateChannel: (id: string, updater: (row: ChannelRow) => ChannelRow) => void;
  addChannel: () => void;
  duplicateChannel: (id: string) => void;
  removeChannel: (id: string) => void;
  bulkUpdateChannels: (ids: string[], updater: (row: ChannelRow) => ChannelRow) => void;
  reset: () => void;
  getSnapshot: () => DataloggerFormSnapshot;
};

export const useDataloggerWizardStore = create<WizardStore>((set, get) => ({
  ...initialState(),

  setStep: (step) => set({ step: Math.max(0, Math.min(WIZARD_STEP_LABELS.length - 1, step)) }),

  loadSnapshot: (snapshot, step = 0) =>
    set((s) => {
      revokePreviewUrl(s.basic.deviceImagePreviewUrl);
      return {
        step: Math.max(0, Math.min(WIZARD_STEP_LABELS.length - 1, step)),
        basic: snapshot.basic,
        channels: snapshot.channels,
        hardwareDraft: {
          samplingRateValue: snapshot.hardware.samplingRateValue,
          samplingRatePeriod: snapshot.hardware.samplingRatePeriod,
          transmissionMode: snapshot.hardware.transmissionMode,
          protocolSettings: snapshot.hardware.protocolSettings,
        },
        lifecycle: snapshot.lifecycle,
        alertConditions: syncThresholdAlertConditions(snapshot.channels, snapshot.alerts),
        alertRecipients: snapshot.alertRecipients,
        visualizations: snapshot.visualizations,
        qrPublic: snapshot.qrPublic ?? initialQrPublic(),
        provisioningResult: null,
      };
    }),

  setBasic: (patch) =>
    set((s) => {
      const nextBasic = { ...s.basic, ...patch };
      if (s.basic.deviceImagePreviewUrl !== nextBasic.deviceImagePreviewUrl) {
        revokePreviewUrl(s.basic.deviceImagePreviewUrl);
      }
      return {
        basic: nextBasic,
      };
    }),

  setQrPublic: (patch) => set((s) => ({ qrPublic: { ...s.qrPublic, ...patch } })),

  addTag: (tag) => {
    const t = tag.trim();
    if (!t) {
      return;
    }
    set((s) => ({
      basic: {
        ...s.basic,
        tags: s.basic.tags.includes(t) ? s.basic.tags : [...s.basic.tags, t],
      },
    }));
  },

  removeTag: (tag) =>
    set((s) => ({
      basic: { ...s.basic, tags: s.basic.tags.filter((x) => x !== tag) },
    })),

  setHardwareDraft: (patch) =>
    set((s) => ({
      hardwareDraft: { ...s.hardwareDraft, ...patch },
    })),

  setLifecycle: (patch) =>
    set((s) => ({
      lifecycle: { ...s.lifecycle, ...patch },
    })),

  setProtocolSettings: (patch) =>
    set((s) => ({
      hardwareDraft: {
        ...s.hardwareDraft,
        protocolSettings: { ...s.hardwareDraft.protocolSettings, ...patch },
      },
    })),

  setTransmissionMode: (transmissionMode) =>
    set((s) => ({
      hardwareDraft: { ...s.hardwareDraft, transmissionMode },
    })),

  setAlertCondition: (index, patch) =>
    set((s) => ({
      alertConditions: s.alertConditions.map((alert, alertIndex) =>
        alertIndex === index ? { ...alert, ...patch } : alert,
      ),
    })),

  addAlertCondition: () =>
    set((s) => ({
      alertConditions: [
        ...s.alertConditions,
        initialAlertCondition(s.channels[0]),
      ],
    })),

  removeAlertCondition: (index) =>
    set((s) => ({
      alertConditions: s.alertConditions.filter((_, alertIndex) => alertIndex !== index),
    })),

  setAlertRecipients: (patch) =>
    set((s) => ({
      alertRecipients: { ...s.alertRecipients, ...patch },
    })),

  setVisualizations: (patch) =>
    set((s) => ({
      visualizations: { ...s.visualizations, ...patch },
    })),

  setWindRose: (patch) =>
    set((s) => ({
      visualizations: {
        ...s.visualizations,
        windRose: { ...s.visualizations.windRose, ...patch },
      },
    })),

  setProvisioningResult: (provisioningResult) => set({ provisioningResult }),

  setChannels: (channels) =>
    set((s) => ({
      channels,
      alertConditions: syncThresholdAlertConditions(channels, s.alertConditions),
    })),

  updateChannel: (id, updater) =>
    set((s) => {
      const channels = s.channels.map((c) => (c.id === id ? updater(c) : c));
      return {
        channels,
        alertConditions: syncThresholdAlertConditions(channels, s.alertConditions),
      };
    }),

  addChannel: () =>
    set((s) => {
      const keys = s.channels.map((c) => c.channelKey);
      const channels = [...s.channels, createDefaultChannel(s.channels.length, keys)];
      return {
        channels,
        alertConditions: syncThresholdAlertConditions(channels, s.alertConditions),
      };
    }),

  duplicateChannel: (id) =>
    set((s) => {
      const src = s.channels.find((c) => c.id === id);
      if (!src) {
        return {};
      }
      const peerKeys = s.channels.map((c) => c.channelKey);
      const nameBase = src.name.trim() ? `${src.name.trim()} copy` : "Channel copy";
      const channelKey = nextUniqueChannelKey(nameBase, peerKeys);
      const newId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `ch-dup-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const copy: ChannelRow = {
        ...src,
        id: newId,
        name: nameBase,
        channelKey,
        formula: src.formula ? { ...src.formula } : null,
      };
      const channels = [...s.channels, copy];
      return {
        channels,
        alertConditions: syncThresholdAlertConditions(channels, s.alertConditions),
      };
    }),

  removeChannel: (id) =>
    set((s) => {
      const channels = s.channels.length <= 1 ? s.channels : s.channels.filter((c) => c.id !== id);
      return {
        channels,
        alertConditions: syncThresholdAlertConditions(channels, s.alertConditions),
      };
    }),

  bulkUpdateChannels: (ids, updater) =>
    set((s) => {
      const channels = s.channels.map((c) => (ids.includes(c.id) ? updater(c) : c));
      return {
        channels,
        alertConditions: syncThresholdAlertConditions(channels, s.alertConditions),
      };
    }),

  reset: () =>
    set((s) => {
      revokePreviewUrl(s.basic.deviceImagePreviewUrl);
      return initialState();
    }),

  getSnapshot: () => {
    const { basic, channels, hardwareDraft, lifecycle, alertConditions, alertRecipients, visualizations, qrPublic } = get();
    const samplingRate = samplesPerPeriodToHz(
      hardwareDraft.samplingRateValue,
      hardwareDraft.samplingRatePeriod,
    );
    return {
      basic,
      hardware: {
        channelCount: channels.length,
        samplingRateValue: hardwareDraft.samplingRateValue,
        samplingRatePeriod: hardwareDraft.samplingRatePeriod,
        transmissionMode: hardwareDraft.transmissionMode,
        protocolSettings: hardwareDraft.protocolSettings,
        samplingRate,
      },
      channels,
      lifecycle,
      alerts: syncThresholdAlertConditions(channels, alertConditions).map((alertCondition) => ({
        ...alertCondition,
        channelId:
          alertCondition.channelId && channels.some((c) => c.id === alertCondition.channelId)
            ? alertCondition.channelId
            : channels.find((c) => c.channelKey === alertCondition.channelKey)?.id ?? channels[0]?.id ?? null,
        channelKey:
          channels.some((c) => c.channelKey === alertCondition.channelKey)
            ? alertCondition.channelKey
            : channels[0]?.channelKey ?? "",
      })),
      alertRecipients,
      visualizations,
      qrPublic,
    };
  },
}));

export const WIZARD_STEP_LABELS = [
  "Overview",
  "Channels",
  "Visualizations",
  "Data & sampling",
  "Alerts",
  "Connectivity",
  "Lifecycle",
  "QR / public page",
  "Review",
] as const;
